import {
	GalleryEntryCard,
	GalleryQuickVote,
	VoteOption,
} from "../JudgingInterfaces";
import styled, { css, useTheme, keyframes } from "styled-components";
import { useState, useEffect } from "react";
import {
	getContrastingTextColor,
	isNearBlackColor,
	voteButtonColor,
} from "../../../Judging/GalleryCard";
import SlotCounter from "react-slot-counter";
import {
	areEntryVotesIn,
	getRoundBehavior,
	hasSubmittedVote,
	IN_OUT_ROUND_NAME,
	isAbstainedVote,
	isGrandOrWinnerRound,
	isScoreRound,
	isInOutRound,
	isStatueRound,
	MULTIVARIABLE_ROUND_NAME,
	ONE_TO_TEN_ROUND_NAME,
} from "../judgingRoundUtils";

const voteBoxBgAnimation = (background: string) => keyframes`
    from {
        background: transparent;
    }

    to {
        background: ${background};
    }
`;

const StyledVoteBox = styled.div<{
	background?: string;
	large?: boolean;
	length: number;
	hideCount?: boolean;
	highlighted?: boolean;
	lightOutline?: boolean;
}>`
	box-sizing: border-box;
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
	min-width: 60px;
	width: auto;
	height: 60px;
	padding: 0.25rem 0.4rem;
	gap: 0.2rem;
	background: transparent;

	.score,
	.score * {
		font-size: 24px !important;
		line-height: 24px !important;
		font-weight: 700 !important;
	}

	.award {
		font-size: 10px;
		line-height: 1.1;
		text-align: center;
		white-space: nowrap;
	}

	/* animate bg color */
	${(p) =>
		p.background
			? css`
					font-weight: ${p.theme.fontSemiBold};
					animation: ${voteBoxBgAnimation(p.background)} 750ms ease forwards;
					color: ${getContrastingTextColor(p.background, p.theme)};
					${p.highlighted
						? `border: 0;`
						: isNearBlackColor(p.background)
							? `border: 1px solid ${p.theme.colorCopyLightLight};`
							: ""}
				`
			: `
        border: 1px solid ${
					p.lightOutline ? p.theme.colorCopyLightLight : p.theme.colorBorderLight
				};
    `}

	${(p) =>
		p.large &&
		!p.hideCount &&
		css`
			min-width: 80px;
			height: 80px;

			.score,
			.score * {
				font-size: 2rem !important;
				line-height: 2rem !important;
			}

			.award {
				font-size: 0.75rem;
				line-height: 1.1;
			}
		`}

	${(p) =>
		p.hideCount &&
		css`
			width: ${p.large ? "80px" : "60px"};
			min-width: ${p.large ? "80px" : "60px"};
			height: ${p.large ? "80px" : "60px"};
			padding: 0.25rem;
			gap: 0;

			.award {
				font-size: 12px;
				line-height: 1.1;
				font-weight: ${p.theme.fontSemiBold};
				white-space: normal;
				text-align: center;
			}
		`}
`;

export function initializeGroups(
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
	awardOnly: boolean,
	votedOnly: boolean,
	includeAbstain: boolean,
	includeUnvoted?: boolean,
	scoreAndAbstainOnly?: boolean,
): AwardGroup[] {
	let groups: AwardGroup[] = [];
	if (votedOnly) {
		groups.push({ name: "Voted", count: 0, value: 1, multiplier: 1 });
		if (includeAbstain) {
			groups.push({ name: "Abstain", count: 0, multiplier: 0 });
		}
	} else {
		groups = [
			...voteOptions
				.filter((x) =>
					awardOnly
						? x.isAward
						: x.value || x.value === 0
							? true
							: includeAbstain,
				)
				.map((option) => ({
					name: option.name,
					count: 0,
					value: option.value,
					multiplier: getRoundMultiplier(roundType, roundBehaviorType),
				})),
		];
	}

	if (scoreAndAbstainOnly) {
		groups = groups.filter((x) => x.name === "Abstain");
	}

	if (includeUnvoted) {
		groups.push({
			name: "Unvoted",
			displayName: "Not Voted",
			count: 0,
			multiplier: 0,
		});
	}

	return groups;
}

export function getRoundMultiplier(
	roundType: string,
	roundBehaviorType?: string,
) {
	switch (getRoundBehavior(roundType, roundBehaviorType)) {
		case IN_OUT_ROUND_NAME:
			return 50;
		case ONE_TO_TEN_ROUND_NAME:
		case MULTIVARIABLE_ROUND_NAME:
			return 10;
		default:
			return 1;
	}
}

export function calculateEntryAwardIndicator(
	roundEntry: GalleryEntryCard,
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
	awardOnly: boolean,
	votedOnly: boolean,
): string | undefined {
	// if not every vote has been cast
	// return "Unvoted"
	if (!roundEntry.votes.every((vote) => hasSubmittedVote(vote))) {
		return "Unvoted";
	}

	if (votedOnly) {
		return "Voted";
	}

	// if the entry has already been awarded, return award
	if (roundEntry.awardLevel) {
		return roundEntry.awardLevel;
	}

	// return aggregate score if round type is 1-10
	if (
		isScoreRound(roundType, roundBehaviorType) &&
		roundEntry.score !== undefined
	) {
		return String(roundEntry.score);
	}

	const roundMultiplier = getRoundMultiplier(roundType, roundBehaviorType);

	return voteOptions
		.filter((x) => (awardOnly ? x.isAward : true))
		.find((x) =>
			x.value || x.value === 0
				? roundEntry.score! >= x.value * roundMultiplier
				: true,
		)?.name;
}

export function calculateEntryResultIndicator(
	roundEntry: GalleryEntryCard,
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
): string | undefined {
	const awardIndicator = calculateEntryAwardIndicator(
		roundEntry,
		roundType,
		roundBehaviorType,
		voteOptions,
		false,
		false,
	);

	const matchingVoteOption = voteOptions.find(
		(option) =>
			option.name === awardIndicator || option.awardAlias === awardIndicator,
	);

	return matchingVoteOption?.name || awardIndicator;
}

export function calculateEntryAwardGroups({
	roundEntries,
	roundType,
	roundBehaviorType,
	voteOptions,
	awardOnly,
	votedOnly,
	includeAbstain,
	includeUnvoted,
	scoreAndAbstainOnly,
	entryResultMode,
}: AwardSummaryBoxProps): AwardGroup[] {
	let groups: AwardGroup[] = initializeGroups(
		roundType,
		roundBehaviorType,
		voteOptions,
		awardOnly,
		votedOnly,
		includeAbstain,
		includeUnvoted,
		scoreAndAbstainOnly,
	);

	roundEntries?.forEach((entry) => {
		const award =
			entryResultMode && !votedOnly
				? calculateEntryResultIndicator(
						entry,
						roundType,
						roundBehaviorType,
						voteOptions,
					)
				: calculateEntryAwardIndicator(
						entry,
						roundType,
						roundBehaviorType,
						voteOptions,
						awardOnly,
						votedOnly,
					);
		if (award) {
			const group = groups.find((group) => group.name === award);
			if (group) {
				group.count += 1;
			}
		}
	});

	return groups;
}

export function calculateVoteAwardIndicator(
	vote: GalleryQuickVote,
	awardOnly: boolean,
	votedOnly: boolean,
	includeAbstain?: boolean,
) {
	// if vote has not been cast
	// return "Unvoted"
	if (!hasSubmittedVote(vote)) {
		return "Unvoted";
	}

	if (votedOnly) {
		if (includeAbstain && isAbstainedVote(vote)) {
			return vote.voteOption?.name || "Abstain";
		}
		return "Voted";
	}

	if (isAbstainedVote(vote)) {
		return vote.voteOption?.name || "Abstain";
	}

	if (!vote.voteOption) {
		return undefined;
	}

	if (awardOnly) {
		return vote.voteOption.isAward ? vote.voteOption.name : undefined;
	}

	return vote.voteOption.name;
}

export function calculateVoteAwardGroups({
	roundEntry,
	roundType,
	roundBehaviorType,
	voteOptions,
	awardOnly,
	votedOnly,
	includeAbstain,
	includeUnvoted,
	scoreAndAbstainOnly,
}: AwardSummaryBoxProps): AwardGroup[] {
	let groups: AwardGroup[] = initializeGroups(
		roundType,
		roundBehaviorType,
		voteOptions,
		awardOnly,
		votedOnly,
		includeAbstain,
		includeUnvoted,
		scoreAndAbstainOnly,
	);

	roundEntry?.votes?.forEach((vote) => {
		const award = calculateVoteAwardIndicator(
			vote,
			awardOnly,
			votedOnly,
			includeAbstain,
		);
		if (award) {
			const group = groups.find((group) => group.name === award);
			if (group) {
				group.count += 1;
			}
		}
	});

	return groups;
}

export const VoteBox = (props: {
	score: number;
	scoreLength: number;
	award: string;
	background?: string;
	revealResults: boolean;
	large?: boolean;
	hideCount?: boolean;
	highlighted?: boolean;
}) => {
	const theme = useTheme();
	const isNearBlackAward =
		isNearBlackColor(props.background) ||
		isNearBlackColor(voteButtonColor(props.award, theme));

	return (
		<StyledVoteBox
			background={props.background}
			highlighted={props.highlighted}
			lightOutline={isNearBlackAward && !props.highlighted}
			large={props.large}
			length={props.scoreLength}
			hideCount={props.hideCount}
		>
			{!props.hideCount && (
				<span className="score">
					{props.revealResults ? (
						<SlotCounter value={props.score} dummyCharacterCount={0} />
					) : (
						"-"
					)}
				</span>
			)}
			{props.award ? (
				<span className="award text-center">{props.award}</span>
			) : null}
		</StyledVoteBox>
	);
};

const AwardSummaryBox = (props: AwardSummaryBoxProps) => {
	const theme = useTheme();
	const [awardSummary, setAwardSummary] = useState<AwardGroup[]>([]);
	const requiresEntryVotes =
		!props.entryResultMode &&
		(isInOutRound(props.roundType, props.roundBehaviorType) ||
			isScoreRound(props.roundType, props.roundBehaviorType) ||
			isStatueRound(props.roundType, props.roundBehaviorType));
	const revealResults =
		props.revealResults &&
		(!props.roundEntry ||
			!requiresEntryVotes ||
			areEntryVotesIn(props.roundEntry.votes));

	useEffect(() => {
		if (props.roundEntry && props.roundEntry.votes) {
			setAwardSummary(calculateVoteAwardGroups(props));
		} else if (props.roundEntries) {
			setAwardSummary(calculateEntryAwardGroups(props));
		} else {
			setAwardSummary([]);
		}
	}, [props]);

	const renderVoteBoxes = () => {
		let voteBoxes = <></>;
		if (props.singleResultOnly && props.roundEntry) {
			const allVotesIn =
				props.roundEntry.votes.length > 0 &&
				props.roundEntry.votes.every((vote) => hasSubmittedVote(vote));
			const showResultBadge = allVotesIn && revealResults;

			if (!showResultBadge) {
				const placeholderAward = isGrandOrWinnerRound(
					props.roundType,
					props.roundBehaviorType,
				)
					? props.voteOptions.find((option) => option.isAward)?.name ||
						props.voteOptions.find(
							(option) =>
								option.name === "Grand" || option.name === "Winner",
						)?.name ||
						"Grand"
					: "";

				return (
					<VoteBox
						key={`${props.roundEntry.id}.unvoted`}
						score={0}
						scoreLength={1}
						award={placeholderAward}
						revealResults={false}
						large={props.large}
					/>
				);
			}

			const award = calculateEntryResultIndicator(
				props.roundEntry,
				props.roundType,
				props.roundBehaviorType,
				props.voteOptions,
			);

			return award ? (
				<VoteBox
					key={`${props.roundEntry.id}.${award}`}
					score={0}
					scoreLength={award.length}
					award={award}
					background={voteButtonColor(award, theme)}
					revealResults={false}
					large={props.large}
					hideCount
					highlighted
				/>
			) : (
				voteBoxes
			);
		}

		voteBoxes = (
			<>
				{props.scoreAndAbstainOnly && props.roundEntry && (
					<VoteBox
						key={`${props.roundEntry?.id}.Score`}
						score={props.roundEntry.score!}
						scoreLength={String(props.roundEntry.score!).length}
						award="Score"
						revealResults={revealResults}
						large={props.large}
					/>
				)}
				{awardSummary.flatMap((award, i) => {
					const isWinningAward = Boolean(
						revealResults &&
							props.roundEntry &&
							(props.roundEntry.awardLevel === award.name ||
								award.name ===
									(props.entryResultMode
										? calculateEntryResultIndicator(
												props.roundEntry,
												props.roundType,
												props.roundBehaviorType,
												props.voteOptions,
											)
										: calculateEntryAwardIndicator(
												props.roundEntry,
												props.roundType,
												props.roundBehaviorType,
												props.voteOptions,
												props.awardOnly,
												props.votedOnly,
											))),
					);

					return (
						<VoteBox
							key={`${props.roundEntry?.id}.${award.name}`}
							score={award.count}
							scoreLength={String(award.count).length}
							award={award.displayName || award.name}
							background={
								isWinningAward ? voteButtonColor(award.name, theme) : undefined
							}
							revealResults={revealResults}
							large={props.large}
							hideCount={props.hideCounts}
							highlighted={isWinningAward}
						/>
					);
				})}
			</>
		);
		return voteBoxes;
	};

	return renderVoteBoxes();
};

export default AwardSummaryBox;

export interface AwardGroup {
	name: string;
	displayName?: string;
	count: number;
	value?: number;
	multiplier: number;
}

export interface AwardSummaryBoxProps {
	roundEntry?: GalleryEntryCard;
	roundEntries?: GalleryEntryCard[];
	roundType: string;
	roundBehaviorType?: string;
	voteOptions: VoteOption[];
	awardOnly: boolean;
	votedOnly: boolean;
	includeAbstain: boolean;
	revealResults: boolean;
	includeUnvoted?: boolean;
	scoreAndAbstainOnly?: boolean;
	entryResultMode?: boolean;
	singleResultOnly?: boolean;
	hideCounts?: boolean;
	large?: boolean;
}
